<template>
    <div :class="blockClass(block, index)">
        <!-- Toggle Block -->
        <template v-if="block.type === 'toggle'">
            <div class="twork_toggle-wrapper ce-block__content">
                <div class="twork_toggle-title" @click="toggleContent(block.id)">
                    {{ block.data.text }}
                    <span :class="getCaretClass(block.id)"></span>
                </div>
                <div :id="`toggle-content-${block.id}`" :class="getContentClass(block.id)">
                    <ContentBlock v-for="(item, i) in getToggleItems(index, block.data.items)" :key="item.id"
                        :block="item" :index="index + i + 1" :all-blocks="allBlocks" />
                </div>
            </div>
        </template>



        <template v-else-if="block.type === 'header'">
            <component :is="'h' + block.data.level" :class="['ce-block__content', headerClass(block.data.level)]">
                <div v-html="block.data.text"></div>
            </component>
        </template>


        <!-- Paragraph Block -->
        <template v-else-if="block.type === 'paragraph'">
            <p class="twork_paragraph ce-block__content" v-html="block.data.text"></p>
        </template>

        <!-- List Block -->
        <template v-else-if="block.type === 'list'">
            <ul class="twork_list ce-block__content">
                <li v-for="(item, itemIndex) in block.data.items" :key="itemIndex" v-html="item"
                    class="twork_list-item"></li>
            </ul>
        </template>

        <template v-else-if="block.type === 'alert'">
            <div :class="alertClass(block.data.type, block.data.align)">
                <!-- Icon -->
                <font-awesome-icon :icon="alertIcon(block.data.type)" class="mr-3" />

                <!-- Message -->
                <span v-html="block.data.message"></span>
            </div>
        </template>


        <!-- Quote Block -->
        <template v-else-if="block.type === 'quote'">
            <blockquote class="twork_blockquote ce-block__content">
                <p>{{ block.data.text }}</p>
                <cite v-html="block.data.caption"></cite>
            </blockquote>
        </template>

        <!-- Image Block -->
        <template v-else-if="block.type === 'image'">
            <figure :class="imageClasses(block)" class="ce-block__content">
                <img :src="block.data.file.url" :alt="block.data.caption" class="twork_image">
                <figcaption v-if="block.data.caption" v-html="block.data.caption"></figcaption>
            </figure>
        </template>

        <!-- Table Block -->
        <template v-else-if="block.type === 'table'">
            <table class="twork_table ce-block__content">
                <thead v-if="block.data.withHeadings">
                    <tr>
                        <th v-for="(header, index) in block.data.content[0]" :key="'header-' + index">{{ header }}
                        </th>
                    </tr>
                </thead>
                <tbody>
                    <tr v-for="(row, rowIndex) in block.data.content" :key="'row-' + rowIndex">
                        <td v-for="(cell, cellIndex) in row" :key="'cell-' + cellIndex" v-html="cell"></td>
                    </tr>
                </tbody>
            </table>
        </template>
    </div>
</template>

<script>
import { reactive, defineComponent } from 'vue';
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import { faInfoCircle, faCheckCircle, faExclamationTriangle, faTimesCircle } from '@fortawesome/free-solid-svg-icons';
export default defineComponent({
    name: 'ContentBlock',
    components: {
        FontAwesomeIcon
    },
    props: {
        block: {
            type: Object,
            required: true,
        },
        index: {
            type: Number,
            required: true,
        },
        allBlocks: {
            type: Array,
            required: true,
        },
    },
    setup() {
        const toggles = reactive({}); // Track toggle states
        return { toggles };
    },
    methods: {

        alertClass(type, align) {
            const baseClasses = 'p-4 rounded border-l-4 flex items-center';
            const alignClass = align === 'right' ? 'text-right' : 'text-left';

            const typeClasses = {
                info: 'bg-blue-50 border-blue-500 text-blue-700',
                success: 'bg-green-50 border-green-500 text-green-700',
                warning: 'bg-yellow-50 border-yellow-500 text-yellow-700',
                error: 'bg-red-50 border-red-500 text-red-700',
            };

            return `${baseClasses} ${typeClasses[type] || 'bg-gray-50 border-gray-500 text-gray-700'} ${alignClass}`;
        },
        alertIcon(type) {
            const icons = {
                info: faInfoCircle,
                success: faCheckCircle,
                warning: faExclamationTriangle,
                error: faTimesCircle,
            };
            return icons[type] || faInfoCircle;
        },

        toggleContent(id) {
            if (!this.toggles[id]) {
                this.toggles[id] = false; // Initialize if not present
            }
            this.toggles[id] = !this.toggles[id]; // Toggle the state
        },
        getCaretClass(id) {
            return this.toggles[id] ? 'twork_caret_up' : 'twork_caret_down';
        },
        getContentClass(id) {
            return this.toggles[id] ? 'twork_toggle-content active' : 'twork_toggle-content';
        },
        getToggleItems(startIndex, itemsCount) {
            // Return the subsequent blocks that should be included in the toggle
            return this.allBlocks.slice(startIndex + 1, startIndex + 1 + itemsCount);
        },
        blockClass(block, index) {
            return {
                'twork_block': true,
                'twork_block:first-child': index === 0
            };
        },
        headerClass(level) {
            const styles = {
                1: 'text-[32px] leading-tight font-extrabold', // Compact line height for large headers
                2: 'text-[24px] leading-tight font-bold',
                3: 'text-[18px] leading-relaxed font-bold', // Relaxed line height for smaller headers
                4: 'text-[16px] leading-relaxed font-semibold',
                5: 'text-[14px] leading-relaxed font-semibold',
                6: 'text-[14px] leading-relaxed font-semibold',
            };
            return styles[level] || 'text-[14px] leading-relaxed font-normal';
        },
        imageClasses(block) {
            return [
                block.data.stretched ? 'twork_img-fullwidth' : '',
                block.data.withBorder ? 'twork_img-border' : '',
                block.data.withBackground ? 'twork_img-bg' : '',
            ].join(' ');
        },
    },
});
</script>

<style scoped>
.twork_block {
    margin: 15px 0;
}

.twork_block:first-child {
    margin-top: 0;
}

.ce-block__content p,
.ce-block__content ce-paragraph {
    @apply tracking-wider text-[12px] text-gray-500;
}

.ce-block__content h1 {
    @apply text-[32px] leading-5 font-extrabold;
}

.ce-block__content h2 {
    @apply text-[24px] leading-5 font-bold;
}

.ce-block__content h3 {
    @apply text-[18px] leading-5 font-bold;
}

.ce-block__content h4 {
    @apply text-[16px] leading-5 font-semibold;
}

.ce-block__content h5 {
    @apply text-[14px] leading-5 font-semibold;
}

.ce-block__content h6 {
    @apply text-[14px] leading-5 font-semibold;
}

.twork_paragraph {
    font-family: 'Roboto', sans-serif;
    line-height: 1.5;
    color: #444;
}

.twork_list {
    list-style-type: disc;
    padding-left: 20px;
    margin: 10px 0;
}

.twork_list-item {
    padding: 5px 0;
}

.twork_image {
    display: block;
    max-width: 100%;
    height: auto;
    cursor: pointer;
}

.twork_img-fullwidth {
    width: 100%;
}

.twork_img-border {
    border: 1px solid #ddd;
    padding: 8px;
    border-radius: 8px;
}

.twork_img-bg {
    background-color: #f4f4f4;
    padding: 15px;
}

.twork_blockquote {
    padding: 10px 20px;
    position: relative;
    font-style: italic;
    background-color: #f9f9f9;
    border-left: 5px solid #ccc;
    border-radius: 8px;
}

.twork_table {
    width: 100%;
    border-collapse: collapse;
    margin: 20px 0;
}

.twork_table th,
.twork_table td {
    border: 1px solid #ddd;
    padding: 8px;
    text-align: left;
}

.twork_toggle-wrapper {
    padding: 8px;
    background-color: #ffffff;
    border: 1px solid #ddd;
    cursor: pointer;
    border-radius: 8px;
    box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
    transition: box-shadow 0.3s ease;
}

.twork_toggle-wrapper:hover {
    box-shadow: 0 8px 16px rgba(0, 0, 0, 0.2);
}

.twork_toggle-title {
    color: #0056b3;
    font-weight: bold;
    padding: 10px;
    display: flex;
    justify-content: space-between;
    align-items: center;
}

.twork_caret_down::after {
    content: '';
    display: inline-block;
    width: 20px;
    height: 20px;
    background-image: url(https://static.ne.twork.cz/repo/content/linker/icons/ui/chevron-down.svg);
    background-size: contain;
    background-repeat: no-repeat;
    transition: transform 0.3s ease;
}

.twork_caret_up::after {
    content: '';
    display: inline-block;
    width: 20px;
    height: 20px;
    background-image: url(https://static.ne.twork.cz/repo/content/linker/icons/ui/chevron-up.svg);
    background-size: contain;
    background-repeat: no-repeat;
    transition: transform 0.3s ease;
}

.twork_toggle-content {
    display: none;
    padding-left: 10px;
}

.twork_toggle-content.active {
    display: block;
    box-shadow: 0 8px 16px rgba(0, 0, 0, 0.2);
}
</style>
